import PropTypes from "prop-types";
import React, { Component } from "react";
import {
  Header,
  Icon,
  Label,
  Message,
  Popup,
  Button,
  Modal,
  Form,
  Input,
  Divider,
} from "semantic-ui-react";
import LoansListItem from "../LoansListEntry";
import { BrwReqLoanExtendButton } from "./BrwReqLoanExtendButton";
import { LoanExtendButton } from "./LoanExtendButton";
import OverdueSlipModal from "@pages/backoffice/Fee/Modal/OverdueSlipModal/OverdueSlipModal";
import PaymentModal from "@pages/backoffice/Fee/Modal/PaymentSubmissionModal/PaymentModal";
import { patronApi } from "@api/patrons";
import { fineApi } from "@api/fine/fine";
import {
  calculateOverdueDays,
  calculateBusinessDays,
  formatDateForDisplay,
  handleFileUpload,
  processLoanStatusUpdate,
  createPaymentFormData,
  createSafeStateUpdater,
  DEFAULT_MESSAGES,
  FINE_RATE_PER_DAY,
  REFRESH_DELAY_AFTER_PAYMENT,
} from "@utils/fineUtils";

export default class LoansListEntry extends Component {
  constructor(props) {
    super(props);
    this.state = {
      actionMsgOpened: false,
      actionMsg: "",
      overdueSlipModalOpen: false,
      paymentModalOpen: false,
      submitReceiptModalOpen: false,
      receiptFile: null,
      orReferenceNumber: "",
      receiptSubmitLoading: false,
      overdueModalOverrideData: null,
      paymentImagePreview: null,
      isPaymentLoading: false,
      paymentSelectedBooks: [],
      loanData: props.loan,
      patronData: null,
      patronLoading: false,
      patronError: null,
      fineRecord: null,
      fineLoading: false,
      effectiveFineAmount: null,
    };

    this.safeSetState = createSafeStateUpdater(this);
  }

  componentDidMount() {
    this._isMounted = true;
    const { loan } = this.props;
    this.fetchPatronDetails(loan.metadata.patron_pid);
    // Fetch fine record if loan is overdue
    if (loan.metadata.is_overdue) {
      this.fetchFineRecord(loan.metadata.pid);
    }
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.loan.metadata.patron_pid !== prevProps.loan.metadata.patron_pid
    ) {
      this.fetchPatronDetails(this.props.loan.metadata.patron_pid);
    }

    // Refetch fine if loan becomes overdue
    if (
      this.props.loan.metadata.is_overdue &&
      !prevProps.loan.metadata.is_overdue
    ) {
      this.fetchFineRecord(this.props.loan.metadata.pid);
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  fetchFineRecord = async (loanId) => {
    if (!loanId) return;

    this.safeSetState({ fineLoading: true });

    try {
      const response = await fineApi.getFinesByLoanId(loanId);

      if (response.data?.hits?.length > 0) {
        // Get the active fine record
        const activeFine = response.data.hits.find(
          (fine) => fine.status === "active" || fine.status === "unpaid"
        );

        if (activeFine) {
          const fineAmount = parseFloat(
            activeFine.total_fine || activeFine.calculated_fine || 0
          );

          this.safeSetState({
            fineRecord: activeFine,
            effectiveFineAmount: fineAmount,
            fineLoading: false,
          });

          return;
        }
      }

      // No fine record found, use calculated amount
      const calculatedAmount = this.calculateOverdueFineFromDate();
      this.safeSetState({
        fineRecord: null,
        effectiveFineAmount: calculatedAmount,
        fineLoading: false,
      });
    } catch (error) {
      // Fall back to calculated amount
      const calculatedAmount = this.calculateOverdueFineFromDate();
      this.safeSetState({
        fineRecord: null,
        effectiveFineAmount: calculatedAmount,
        fineLoading: false,
      });
    }
  };

  fetchPatronDetails = async (patronPid) => {
    if (!patronPid) return;
    this.safeSetState({ patronLoading: true, patronError: null });

    try {
      const response = await patronApi.get(patronPid);
      this.safeSetState({ patronData: response.data, patronLoading: false });
    } catch (error) {
      this.safeSetState({
        patronError: DEFAULT_MESSAGES.ERROR_PATRON,
        patronLoading: false,
      });
    }
  };

  showSuccess = (msg) => {
    if (this._isMounted) this.props.onSuccess(msg);
  };

  showError = (msg) => {
    this.safeSetState({
      actionMsgOpened: true,
      actionMsg: (
        <Message negative>
          <Message.Header>Request failed!</Message.Header>
          {msg}
        </Message>
      ),
    });
  };

  hideError = () => {
    this.safeSetState({ actionMsgOpened: false, actionMsg: "" });
  };

  toggleModal = (modalName, isOpen = null) => {
    this.safeSetState({
      [modalName]: isOpen !== null ? isOpen : !this.state[modalName],
    });
  };

  handleGenerateOverdueSlip = () =>
    this.toggleModal("overdueSlipModalOpen", true);

  handleCloseOverdueSlipModal = async (overrideData = null) => {
    this.toggleModal("overdueSlipModalOpen", false);

    // Refetch the fine record after closing
    if (this.props.loan.metadata.is_overdue) {
      await this.fetchFineRecord(this.props.loan.metadata.pid);
    }
  };

  handleOverrideUpdate = (overrideData) => {
    this.safeSetState({
      overdueModalOverrideData: overrideData,
    });

    // Also update the effective amount
    if (overrideData?.useManualTotal) {
      this.safeSetState({
        effectiveFineAmount: parseFloat(overrideData.totalAmount || 0),
      });
    } else if (overrideData?.overridesByLoanId) {
      const loanId = this.props.loan.metadata.pid;
      if (overrideData.overridesByLoanId[loanId] !== undefined) {
        this.safeSetState({
          effectiveFineAmount: parseFloat(
            overrideData.overridesByLoanId[loanId]
          ),
        });
      }
    }
  };

  handleOpenPaymentModal = () => {
    this.safeSetState({
      paymentModalOpen: true,
      paymentSelectedBooks: [0],
    });
  };

  handleClosePaymentModal = () => {
    this.safeSetState({
      paymentModalOpen: false,
      paymentImagePreview: null,
      isPaymentLoading: false,
      paymentSelectedBooks: [],
    });
  };

  handlePaymentImageUpload = (imageFile) => {
    handleFileUpload(
      imageFile,
      (result) => this.safeSetState({ paymentImagePreview: result }),
      this.showError
    );
  };

  handlePaymentBookSelection = (bookIndex, isSelected) => {
    if (!this._isMounted) return;
    const { paymentSelectedBooks } = this.state;
    const newSelectedBooks = isSelected
      ? [...paymentSelectedBooks, bookIndex]
      : paymentSelectedBooks.filter((index) => index !== bookIndex);
    this.safeSetState({ paymentSelectedBooks: newSelectedBooks });
  };

  handlePaymentSelectAll = () => {
    this.safeSetState({ paymentSelectedBooks: [0] });
  };

  handlePaymentDeselectAll = () => {
    this.safeSetState({ paymentSelectedBooks: [] });
  };

  handlePayFine = async () => {
    const { loanData, paymentImagePreview, paymentSelectedBooks } = this.state;
    const { onSuccess, onError } = this.props;

    // Remove image validation - only check for selected books
    if (!paymentSelectedBooks || paymentSelectedBooks.length === 0) {
      this.showError("Please select at least one book to pay fine for.");
      return;
    }

    this.safeSetState({ isPaymentLoading: true });

    try {
      const overdueFine = this.getEffectiveOverdueFine();
      createPaymentFormData({
        patronId: loanData.metadata.patron_pid,
        amount: overdueFine,
        selectedLoanIds: paymentSelectedBooks.map((index) =>
          loanData.loans
            ? loanData.loans[index]?.metadata.pid
            : loanData.metadata.pid
        ),
        loanAmountDetails: paymentSelectedBooks.map((bookIndex) => {
          const loan = loanData.loans ? loanData.loans[bookIndex] : loanData;
          const calculatedAmount = overdueFine;
          return {
            loanId: loan.metadata.pid,
            bookIndex,
            calculatedAmount,
            overrideAmount: calculatedAmount,
            finalAmount: calculatedAmount,
          };
        }),
        paymentReceipt: paymentImagePreview || null, // Make receipt optional
      });

      const updateResult = await processLoanStatusUpdate(loanData.metadata.pid);

      if (updateResult.success) {
        this.safeSetState({ loanData: updateResult.updatedLoan || loanData });
        this.showSuccess(
          `${DEFAULT_MESSAGES.SUCCESS_WITH_UPDATE} (${updateResult.action}).`
        );
        if (onSuccess) onSuccess(DEFAULT_MESSAGES.SUCCESS_PAYMENT);
      } else {
        this.showSuccess(
          `${DEFAULT_MESSAGES.SUCCESS_PAYMENT}! ${DEFAULT_MESSAGES.LOAN_PROCESSING}`
        );
        if (onSuccess) onSuccess(DEFAULT_MESSAGES.SUCCESS_PAYMENT);
      }

      this.handleClosePaymentModal();
      setTimeout(() => window.location.reload(), REFRESH_DELAY_AFTER_PAYMENT);
    } catch (error) {
      this.showError(DEFAULT_MESSAGES.ERROR_PAYMENT);
      if (onError) onError(error);
    } finally {
      this.safeSetState({ isPaymentLoading: false });
    }
  };

  getBookFineAmount = (bookIndex) => {
    const { loanData } = this.state;
    if (loanData.loans?.[bookIndex]) {
      return loanData.loans[bookIndex].calculatedFine || 0;
    }
    return this.getEffectiveOverdueFine();
  };

  handleSubmitReceipt = () => this.toggleModal("submitReceiptModalOpen", true);

  handleCloseSubmitReceiptModal = () => {
    this.safeSetState({
      submitReceiptModalOpen: false,
      receiptFile: null,
      orReferenceNumber: "",
      receiptSubmitLoading: false,
    });
  };

  handleFileUpload = (event) => {
    const file = event.target.files[0];
    this.safeSetState({ receiptFile: file });
  };

  handleORNumberChange = (event) => {
    this.safeSetState({ orReferenceNumber: event.target.value });
  };

  calculateOverdueDays = () => {
    const { loanData } = this.state;
    return calculateOverdueDays(loanData.metadata.end_date);
  };

  calculateOverdueFineFromDate = () => {
    const { loanData } = this.state;

    if (!loanData?.metadata?.end_date) return 0;

    const dueDate = new Date(loanData.metadata.end_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    dueDate.setHours(0, 0, 0, 0);

    if (today <= dueDate) return 0;

    const businessDays = calculateBusinessDays(dueDate, today);
    return businessDays * FINE_RATE_PER_DAY;
  };

  // Returns the effective fine (database override or calculated)
  getEffectiveOverdueFine = () => {
    const { effectiveFineAmount } = this.state;

    // If we have an effective amount set (from database or override), use it
    if (effectiveFineAmount !== null) {
      return effectiveFineAmount;
    }

    // Otherwise calculate
    return this.calculateOverdueFineFromDate();
  };

  calculateOverdueFine = () => {
    return this.getEffectiveOverdueFine();
  };

  formatPatronForModal = () => {
    const { loanData, patronData, overdueModalOverrideData, fineRecord } =
      this.state;

    const overdueFine = this.getEffectiveOverdueFine();

    const formatted = {
      pid: loanData.metadata.patron_pid,
      name:
        patronData?.metadata?.full_name ||
        patronData?.metadata?.name ||
        loanData.metadata.patron?.name ||
        "Unknown Patron",
      email:
        patronData?.metadata?.email ||
        loanData.metadata.patron?.email ||
        "No email available",
      totalFine: overdueFine,
      overdueCount: 1,
      overrideData: overdueModalOverrideData,
      loans: [
        {
          metadata: {
            pid: loanData.metadata.pid,
            document: {
              title: loanData.metadata.document?.title || "Unknown Title",
              edition: loanData.metadata.document?.edition || "",
            },
            item: {
              barcode: loanData.metadata.item?.barcode || "N/A",
            },
            end_date: loanData.metadata.end_date,
            patron: {
              pid: loanData.metadata.patron_pid,
              name:
                patronData?.metadata?.full_name ||
                patronData?.metadata?.name ||
                loanData.metadata.patron?.name,
              email:
                patronData?.metadata?.email || loanData.metadata.patron?.email,
            },
          },
          calculatedFine: overdueFine,
        },
      ],
      fineRecords: fineRecord ? [fineRecord] : [],
    };

    return formatted;
  };

  renderLoanStatusLabel = () => {
    const { loanData, fineRecord } = this.state;
    const { metadata } = loanData;
    const isOverdue = metadata.is_overdue;

    if (isOverdue) {
      const calculatedFine = this.calculateOverdueFineFromDate();
      const displayFine = this.getEffectiveOverdueFine();
      const isOverridden = fineRecord && displayFine !== calculatedFine;

      return (
        <div>
          <h4>
            Your loan is overdue. Please return the literature as soon as
            possible!
          </h4>
          <div style={{ marginTop: "10px", fontSize: "1.1em" }}>
            <strong>Fine Amount: ₱{displayFine.toFixed(2)}</strong>
            {isOverridden && (
              <span style={{ marginLeft: "8px" }}>
                <Label size="mini" color="orange">
                  OVERRIDDEN
                </Label>
                <span
                  style={{
                    fontSize: "0.85em",
                    color: "#666",
                    marginLeft: "4px",
                  }}
                >
                  (was ₱{calculatedFine.toFixed(2)})
                </span>
              </span>
            )}
          </div>
        </div>
      );
    }

    return (
      <h4>
        Please return the literature by
        <Header size="large">{formatDateForDisplay(metadata.end_date)}</Header>
      </h4>
    );
  };

  renderOngoingLabel = () => {
    const { loanData } = this.state;
    const { metadata } = loanData;
    const isIllBrwReq = metadata.item_pid.type === "illbid";

    return (
      <div className="pt-default">
        <Label basic>
          Loaned on
          <Label.Detail>
            {formatDateForDisplay(metadata.start_date)}
          </Label.Detail>
        </Label>
        {isIllBrwReq && (
          <Popup
            content="This loan involves third party library, please return on time."
            trigger={
              <Icon name="exclamation circle" size="large" color="red" />
            }
          />
        )}
      </div>
    );
  };

  renderExtraActions = () => {
    const { loanData } = this.state;
    const { metadata } = loanData;
    const isLoanOverdue = metadata.is_overdue;
    const isIllBrwReq = metadata.item_pid.type === "illbid";

    if (isLoanOverdue) {
      return (
        <div>
          <Button
            color="orange"
            icon="file alternate"
            content="Generate Overdue Slip"
            onClick={this.handleGenerateOverdueSlip}
            style={{ marginRight: "10px" }}
          />
          <Button
            color="green"
            icon="upload"
            content="Upload Receipt"
            onClick={this.handleOpenPaymentModal}
          />
        </div>
      );
    }

    if (isIllBrwReq) {
      return (
        <BrwReqLoanExtendButton
          loan={loanData}
          onSuccess={this.showSuccess}
          onError={this.showError}
        />
      );
    }

    return (
      <LoanExtendButton
        loan={loanData}
        onSuccess={this.showSuccess}
        onError={this.showError}
      />
    );
  };

  renderPatronInfo = () => {
    const { patronLoading, patronError } = this.state;

    if (patronLoading) return <p>Loading patron info...</p>;
    if (patronError) return <p style={{ color: "red" }}>{patronError}</p>;
    return <div style={{ marginBottom: "1em" }}> </div>;
  };

  renderSubmitReceiptModal = () => {
    const {
      submitReceiptModalOpen,
      receiptFile,
      orReferenceNumber,
      receiptSubmitLoading,
    } = this.state;
    const overdueFine = this.getEffectiveOverdueFine();

    return (
      <Modal
        open={submitReceiptModalOpen}
        onClose={this.handleCloseSubmitReceiptModal}
        size="small"
      >
        <Modal.Header>
          <Icon name="upload" />
          Submit Receipt
        </Modal.Header>
        <Modal.Content>
          <Form>
            <Form.Field>
              <label>Upload Receipt (Optional)</label>
              <Input
                type="file"
                accept="image/*,application/pdf"
                onChange={this.handleFileUpload}
              />
              {receiptFile && (
                <Label color="green" style={{ marginTop: "10px" }}>
                  <Icon name="file" />
                  {receiptFile.name}
                </Label>
              )}
            </Form.Field>
            <Divider horizontal>OR</Divider>
            <Form.Field>
              <label>OR/Reference Number</label>
              <Input
                placeholder="Enter OR or Reference Number"
                value={orReferenceNumber}
                onChange={this.handleORNumberChange}
              />
            </Form.Field>
            <Message info>
              <Message.Header>Payment Information</Message.Header>
              <p>
                Fine Amount: <strong>₱{overdueFine.toFixed(2)}</strong>
              </p>
              <p>
                Please provide either a receipt file or OR/Reference number as
                proof of payment.
              </p>
            </Message>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button
            color="green"
            icon="check"
            content="Submit"
            loading={receiptSubmitLoading}
            disabled={!receiptFile && !orReferenceNumber}
            onClick={this.handleReceiptSubmit}
          />
          <Button
            color="grey"
            content="Cancel"
            onClick={this.handleCloseSubmitReceiptModal}
          />
        </Modal.Actions>
      </Modal>
    );
  };

  render() {
    const {
      actionMsg,
      actionMsgOpened,
      overdueSlipModalOpen,
      paymentModalOpen,
      overdueModalOverrideData,
      paymentImagePreview,
      isPaymentLoading,
      loanData,
      fineLoading,
    } = this.state;

    const isLoanOverdue = loanData.metadata.is_overdue;
    const overdueFine = this.getEffectiveOverdueFine();
    const formattedPatron = this.formatPatronForModal();

    const selectedBooksForModal = [
      {
        loanId: loanData.metadata.pid,
        title: loanData.metadata.document?.title || "Unknown Title",
        itemTitle: loanData.metadata.document?.title || "Unknown Title",
        dueDate: loanData.metadata.end_date,
        itemBarcode: loanData.metadata.item?.barcode,
        calculatedFine: overdueFine,
        metadata: {
          pid: loanData.metadata.pid,
          document: loanData.metadata.document,
          end_date: loanData.metadata.end_date,
          item: loanData.metadata.item,
        },
      },
    ];

    return (
      <>
        {this.renderPatronInfo()}

        {fineLoading && (
          <Message info size="small">
            <Icon name="spinner" loading />
            Loading fine information...
          </Message>
        )}

        <LoansListItem
          loan={loanData}
          extraItemProps={{
            itemClass: isLoanOverdue ? "bkg-danger" : null,
            itemMetaCmp: this.renderOngoingLabel(),
            itemDescriptionCmp: this.renderLoanStatusLabel(),
            itemExtraCmp: (
              <Popup
                trigger={this.renderExtraActions()}
                content={actionMsg}
                position="top center"
                open={actionMsgOpened}
                wide
                onClick={this.hideError}
              />
            ),
          }}
        />

        <OverdueSlipModal
          isOpen={overdueSlipModalOpen}
          selectedPatron={formattedPatron}
          onClose={this.handleCloseOverdueSlipModal}
          onOverrideUpdate={this.handleOverrideUpdate}
          existingOverrideData={overdueModalOverrideData}
          allowOverride={false}
        />

        <PaymentModal
          isOpen={paymentModalOpen}
          selectedPatron={formattedPatron}
          overrideData={overdueModalOverrideData}
          calculatedTotal={overdueFine}
          isUsingOverrides={!!overdueModalOverrideData}
          selectedBooks={selectedBooksForModal}
          paymentImagePreview={paymentImagePreview}
          isPaymentLoading={isPaymentLoading}
          isManualTotal={false}
          onClose={this.handleClosePaymentModal}
          onBookSelection={this.handlePaymentBookSelection}
          onSelectAll={this.handlePaymentSelectAll}
          onDeselectAll={this.handlePaymentDeselectAll}
          onImageUpload={this.handlePaymentImageUpload}
          onPayFine={this.handlePayFine}
          getBookFineAmount={this.getBookFineAmount}
        />

        {this.renderSubmitReceiptModal()}
      </>
    );
  }
}

LoansListEntry.propTypes = {
  loan: PropTypes.object.isRequired,
  onSuccess: PropTypes.func.isRequired,
  onError: PropTypes.func,
};

LoansListEntry.defaultProps = {
  onError: () => {},
};
